// 권위:
//   - data/reports/UNIMPLEMENTED_INVENTORY.md §4.3 (트레이딩 신규 화면 권고)
//   - src/agents/trading/persistence/position_repo.py::PositionStateRow (분류 B audit + state)
//   - src/agents/trading/execution/types.py::QueuedOrder (S1/S2/S3 unfilled stage)
//   - src/agents/trading/events/event_codes.py (TRADING.SIGNAL.* / TRADING.SLOT.*)
//
// 본 모듈은 트레이딩 화면이 사용하는 *UI 타입 정의* 의 권위 (백엔드 DTO ↔ 화면 정합점).
// 실 API 호출은 `tradingApi.ts`, raw → UI 매핑은 `tradingMappers.ts` 가 담당한다.
//
// MOCK_POSITIONS / MOCK_UNFILLED 는 운영 API (TradingController) 신설 완료에 따라
// 제거됐다. MOCK_SIGNAL_FLOW / MOCK_SLOTS 는 후속 사이클 (TRADING.SIGNAL.* /
// TRADING.SLOT.* WebSocket 토픽 분리) 까지 LiveMonitor 초기 fallback 으로 보존.
//
// 운영 시각화 정합을 위해 컬럼·열 폭은 운영 테이블 (`position_state` /
// `trading_unfilled_orders`) 스키마와 1:1 매칭한다.

export type PositionState =
  | 'INITIAL'
  | 'ACTIVE_FULL'
  | 'ACTIVE_TP1_DONE'
  | 'ACTIVE_TP2_DONE'
  | 'ACTIVE_HALF_STOPPED'
  | 'ACTIVE_FORCED_HALF'
  | 'CLOSED';

/** position_state 테이블 1행 (운영 시각화용 발췌). */
export interface PositionRow {
  symbol: string;
  symbolName: string;
  state: PositionState;
  qty: number;
  avgEntry: number;
  currentPrice: number;
  stopLossPrice: number;
  tp1Price: number | null;
  tp1Done: boolean;
  tp2Price: number | null;
  tp2Done: boolean;
  trailStopPrice: number | null;
  /** 분류 B audit — corporate_actions_audit 마지막 재계산 시각. */
  lastCorpActionRecalcAt: string | null;
  enteredAt: string;
}

export type UnfilledStage = 'S1' | 'S2' | 'S3' | 'D2_OPEN' | 'DISCARDED';

/** trading_unfilled_orders 테이블 1행 (S1/S2/S3 rollover 영속화). */
export interface UnfilledOrderRow {
  rowId: string;
  symbol: string;
  symbolName: string;
  signalKind: 'BUY' | 'SELL' | 'STOP_LOSS' | 'TAKE_PROFIT';
  unfilledQty: number;
  stage: UnfilledStage;
  rolloverCount: number;
  nextActionAt: string;
  createdAt: string;
}

export type SignalStage =
  | 'RECEIVED'
  | 'QUEUED'
  | 'DEFERRED'
  | 'DROPPED'
  | 'PROMOTED'
  | 'STALE_EXPIRED';

export interface SignalFlowItem {
  signalId: string;
  symbol: string;
  signalKind: 'BUY' | 'SELL' | 'STOP_LOSS' | 'TAKE_PROFIT';
  stage: SignalStage;
  reason: string | null;
  occurredAt: string;
}

export type SlotName = 'pre_open' | 'closing_evaluation' | 'post_market_batch';
export type SlotStatus = 'IDLE' | 'STARTED' | 'SUCCEEDED' | 'FAILED' | 'SKIPPED_HOLIDAY';

export interface SlotTriggerRow {
  slot: SlotName;
  status: SlotStatus;
  lastStartedAt: string | null;
  lastFinishedAt: string | null;
  message: string | null;
}

// ── Mock 데이터 ──────────────────────────────────────────────
//
// MOCK_POSITIONS / MOCK_UNFILLED 는 운영 API (TradingController) 신설 후 제거됨.
// (실 API 는 `tradingApi.ts::fetchPositions / fetchUnfilledOrders` 참조.)
// MOCK_SIGNAL_FLOW / MOCK_SLOTS 는 LiveMonitor 의 WebSocket 토픽 swap 이전까지 보존.

const NOW = '2026-05-21T10:14:00+09:00';

export const MOCK_SIGNAL_FLOW: SignalFlowItem[] = [
  {
    signalId: 'sig-1f7a',
    symbol: '005930',
    signalKind: 'BUY',
    stage: 'QUEUED',
    reason: 'admission ok',
    occurredAt: NOW,
  },
  {
    signalId: 'sig-1f7b',
    symbol: '293490',
    signalKind: 'BUY',
    stage: 'DROPPED',
    reason: 'LiquidityGate: bid_amount < 1억',
    occurredAt: '2026-05-21T10:13:42+09:00',
  },
  {
    signalId: 'sig-1f7c',
    symbol: '000660',
    signalKind: 'STOP_LOSS',
    stage: 'PROMOTED',
    reason: 'FR-6 promotion (stop bypasses slippage gate)',
    occurredAt: '2026-05-21T10:12:55+09:00',
  },
  {
    signalId: 'sig-1f7d',
    symbol: '035720',
    signalKind: 'BUY',
    stage: 'DEFERRED',
    reason: 'no_closing_estimate (fallback queued)',
    occurredAt: '2026-05-21T10:12:10+09:00',
  },
];

export const MOCK_SLOTS: SlotTriggerRow[] = [
  {
    slot: 'pre_open',
    status: 'SUCCEEDED',
    lastStartedAt: '2026-05-21T08:50:00+09:00',
    lastFinishedAt: '2026-05-21T08:50:14+09:00',
    message: '14 signals admitted, 3 deferred, 1 dropped',
  },
  {
    slot: 'closing_evaluation',
    status: 'IDLE',
    lastStartedAt: '2026-05-20T15:15:00+09:00',
    lastFinishedAt: '2026-05-20T15:15:08+09:00',
    message: '6 evaluators ok',
  },
  {
    slot: 'post_market_batch',
    status: 'IDLE',
    lastStartedAt: '2026-05-20T16:30:00+09:00',
    lastFinishedAt: '2026-05-20T16:32:21+09:00',
    message: 'trailing+ma20 batch ok',
  },
];

// ── 포지션 손익률 helper (UI 노출용) ──────────────────────────
//
// 시세 source 부재 시 currentPrice 는 NaN — 본 helper 들은 null 반환으로 page 의
// "-" 표시 분기를 트리거한다 (mock 시절 0 폴백은 시세 0원으로 오인 가능).

export function pnlPct(row: PositionRow): number | null {
  if (row.avgEntry <= 0) return null;
  if (!Number.isFinite(row.currentPrice)) return null;
  return ((row.currentPrice - row.avgEntry) / row.avgEntry) * 100;
}

export function distanceToStopPct(row: PositionRow): number | null {
  if (!Number.isFinite(row.currentPrice) || row.currentPrice <= 0) return null;
  return ((row.currentPrice - row.stopLossPrice) / row.currentPrice) * 100;
}
